import React, { useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { normalizeCvacHotelLabel } from "../../../utils/customerChatHotelFilter";

const ALL_LABEL = "כל המלונות";

/**
 * Searchable hotel filter above chat result cards (RTL).
 * @param {{
 *   availableHotels: string[],
 *   selectedHotel: string | null,
 *   onChange: (name: string | null) => void,
 *   statusLine: string | null,
 *   disabled?: boolean,
 * }} props
 */
export default function CustomerVacationChatHotelFilterBar({
  availableHotels,
  selectedHotel,
  onChange,
  statusLine,
  disabled = false,
}) {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetQuery, setSheetQuery] = useState("");

  const options = useMemo(() => {
    const opts = [{ label: ALL_LABEL, value: null }];
    for (const h of availableHotels) {
      if (normalizeCvacHotelLabel(h)) opts.push({ label: h, value: h });
    }
    return opts;
  }, [availableHotels]);

  const selectedOption =
    options.find((o) => o.value === selectedHotel) ?? options[0];

  const sheetFiltered = useMemo(() => {
    const q = normalizeCvacHotelLabel(sheetQuery).toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, sheetQuery]);

  const mobileTriggerLabel = selectedHotel || ALL_LABEL;

  if (isNarrow) {
    return (
      <div className="hr-cvac-hotel-filter">
        <div className="hr-cvac-hotel-filter__label" id="hr-cvac-hotel-filter-label">
          סינון לפי מלון
        </div>
        <button
          type="button"
          className="hr-cvac-hotel-filter__mobile-trigger"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
          aria-labelledby="hr-cvac-hotel-filter-label"
          onClick={() => {
            setSheetQuery("");
            setSheetOpen(true);
          }}
        >
          <span className="hr-cvac-hotel-filter__mobile-trigger-text">{mobileTriggerLabel}</span>
          <span className="hr-cvac-hotel-filter__chevron" aria-hidden>
            ▾
          </span>
        </button>
        {statusLine ? <p className="hr-cvac-hotel-filter__status">{statusLine}</p> : null}
        {sheetOpen ? (
          <div className="hr-cvac-sheet" role="dialog" aria-modal="true" aria-label="סינון לפי מלון">
            <button
              type="button"
              className="hr-cvac-sheet__backdrop"
              onClick={() => setSheetOpen(false)}
              aria-label="סגור"
            />
            <div className="hr-cvac-sheet__panel">
              <div className="hr-cvac-sheet__toolbar">
                <h4 className="hr-cvac-sheet__title">סינון לפי מלון</h4>
                <button
                  type="button"
                  className="hr-cvac-sheet__x"
                  onClick={() => setSheetOpen(false)}
                  aria-label="סגור"
                >
                  ×
                </button>
              </div>
              <div className="hr-cvac-sheet__body hr-cvac-hotel-filter__sheet-body">
                <input
                  type="search"
                  className="hr-cvac-hotel-filter__sheet-search"
                  placeholder="חיפוש שם מלון…"
                  value={sheetQuery}
                  onChange={(e) => setSheetQuery(e.target.value)}
                  autoFocus
                />
                <ul className="hr-cvac-hotel-filter__sheet-list" role="listbox">
                  {sheetFiltered.map((opt) => (
                    <li key={opt.value ?? "__all__"} className="hr-cvac-hotel-filter__sheet-item">
                      <button
                        type="button"
                        className="hr-cvac-hotel-filter__sheet-option"
                        onClick={() => {
                          onChange(opt.value);
                          setSheetOpen(false);
                          setSheetQuery("");
                        }}
                      >
                        {opt.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="hr-cvac-hotel-filter">
      <label className="hr-cvac-hotel-filter__label" htmlFor="hr-cvac-hotel-filter-ac">
        סינון לפי מלון
      </label>
      <Autocomplete
        id="hr-cvac-hotel-filter-ac"
        disabled={disabled}
        options={options}
        value={selectedOption}
        onChange={(_, v) => onChange(v?.value ?? null)}
        getOptionLabel={(o) => o?.label ?? ""}
        isOptionEqualToValue={(a, b) => a?.value === b?.value}
        size="small"
        disablePortal={false}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#fff",
            fontSize: "0.875rem",
          },
        }}
        slotProps={{
          popper: {
            sx: { zIndex: 10060 },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="בחרו או חפשו מלון"
            inputProps={{
              ...params.inputProps,
              dir: "rtl",
            }}
          />
        )}
      />
      {statusLine ? <p className="hr-cvac-hotel-filter__status">{statusLine}</p> : null}
    </div>
  );
}
